import * as React from 'react';
import { Card, ActionButton, Table, Input, Modal, Radio, message, Popconfirm } from '@ui';
import { apiRequest, API_BASE } from '@lib/api';
import { t } from '@lib/i18n';
import { MaskedValueCell } from './MaskedValueCell';

/**
 * 堡垒密钥管理（Proxy Auth Key）
 *
 * 对接后端 /api/proxy-auth-keys：
 *   GET    /api/proxy-auth-keys            列表
 *   POST   /api/proxy-auth-keys            创建（自动生成密钥对 / 使用已有公钥）
 *   DELETE /api/proxy-auth-keys/:id        删除
 *   PUT    /api/proxy-auth-keys/:id        更新名称
 *
 * 该界面从原 V2 的 proxy-key 模块移植、简化到 V1。
 */

interface ProxyAuthKey {
  id: number;
  user_id: number;
  key_name: string;
  public_key: string;
  created_at: string;
}

interface ProxyKeyListResp {
  keys?: ProxyAuthKey[];
  error?: string;
}

interface ProxyKeyOpResp {
  id?: number;
  key_name?: string;
  public_key?: string;
  private_key_download?: string;
  uploaded?: boolean;
  error?: string;
}

export const FortressKeyManagement: React.FC = () => {
  const [keys, setKeys] = React.useState<ProxyAuthKey[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  // 创建表单
  const [keyName, setKeyName] = React.useState('');
  const [createMode, setCreateMode] = React.useState<'auto' | 'existing'>('auto');
  const [publicKeyText, setPublicKeyText] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { response, data } = await apiRequest<ProxyKeyListResp>(`${API_BASE}/proxy-auth-keys`);
      if (response.ok) {
        setKeys(data.keys || []);
      } else {
        message.error(data.error || t('proxyKeyManagement.loadFailed'));
      }
    } catch {
      message.error(t('proxyKeyManagement.networkError'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setKeyName('');
    setCreateMode('auto');
    setPublicKeyText('');
    setError('');
    setModalOpen(true);
  };

  const save = async () => {
    if (!keyName.trim()) {
      setError(t('proxyKeyManagement.keyNameRequired'));
      return;
    }
    if (createMode === 'existing' && !publicKeyText.trim()) {
      setError(t('proxyKeyManagement.publicKeyRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body: Record<string, unknown> = { key_name: keyName };
      if (createMode === 'existing') body.public_key = publicKeyText;
      const { response, data } = await apiRequest<ProxyKeyOpResp>(`${API_BASE}/proxy-auth-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        // 自动生成密钥对时，后端会返回 private_key_download 链接，立即触发浏览器下载私钥
        if (createMode === 'auto' && data.private_key_download) {
          try {
            await downloadPrivateKeyFile(data.private_key_download, data.key_name || keyName);
            message.success(t('proxyKeyManagement.createSuccess'));
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            message.error(t('proxyKeyManagement.downloadFailed', { error: msg }));
            // 下载失败时不关闭弹窗，让用户重试或联系管理员
            setSaving(false);
            return;
          }
        } else {
          message.success(t('proxyKeyManagement.createSuccess'));
        }
        setModalOpen(false);
        load();
      } else {
        setError(data.error || t('proxyKeyManagement.createFailed'));
      }
    } catch {
      setError(t('proxyKeyManagement.networkError'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * 从后端下载私钥文件并触发浏览器下载
   * 复用 V2 ProxyKeyManagement 的安全检查（Content-Type/内容合法性）
   */
  const downloadPrivateKeyFile = async (downloadPath: string, fileKeyName: string) => {
    const url = `${API_BASE}${downloadPath}`;
    const fileName = `id_ed25519_${fileKeyName}`;
    const resp = await fetch(url, { credentials: 'same-origin' });
    if (!resp.ok) {
      const errData = await resp.json().catch(() => null);
      throw new Error(errData?.message || `HTTP ${resp.status}`);
    }
    const contentType = resp.headers.get('Content-Type') || '';
    if (contentType.includes('text/html')) {
      throw new Error(t('proxyKeyManagement.networkError'));
    }
    const text = await resp.text();
    if (!text.includes('BEGIN') || !text.includes('PRIVATE KEY')) {
      throw new Error('下载内容格式错误');
    }
    const blob = new Blob([text], { type: 'application/octet-stream' });
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  };

  const remove = async (id: number) => {
    const { response } = await apiRequest(`${API_BASE}/proxy-auth-keys/${id}`, { method: 'DELETE' });
    if (response.ok) {
      message.success(t('proxyKeyManagement.deleteSuccess'));
      load();
    } else {
      message.error(t('proxyKeyManagement.deleteFailed'));
    }
  };

  const columns = [
    { title: t('proxyKeyManagement.keyName'), dataIndex: 'key_name', key: 'key_name' },
    {
      title: t('proxyKeyManagement.publicKey'),
      dataIndex: 'public_key',
      key: 'public_key',
      // 公钥较长，脱敏保留更长前缀便于识别；行为与安装令牌一致
      render: (v: string) => <MaskedValueCell value={v} head={28} tail={6} />,
    },
    { title: t('proxyKeyManagement.createdAt'), dataIndex: 'created_at', key: 'created_at' },
    {
      title: t('common.operation'),
      key: 'action',
      width: 140,
      render: (_: unknown, row: ProxyAuthKey) => (
        <Popconfirm
        title={t('proxyKeyManagement.confirmDelete', { name: row.key_name })}
        onConfirm={() => remove(row.id)}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        align="right"
        >
          <ActionButton action="delete" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
    <Card
      title={
        <div>
          <div className="text-base font-semibold text-white">{t('proxyKeyManagement.fortressKey')}</div>
          <p className="text-xs text-gray-400 mt-1 font-normal">{t('proxyKeyManagement.fortressKeyTip')}</p>
        </div>
      }
      extra={
        <ActionButton action="create" variant="primary" onClick={openCreate}>
          {t('proxyKeyManagement.createProxyKey')}
        </ActionButton>
      }
    >
      <Table rowKey="id" columns={columns} dataSource={keys} loading={loading} style={{ overflow: 'visible' }} />
    </Card>

      <Modal
        title={t('proxyKeyManagement.createProxyKey')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnClose
        footer={
          <div className="flex justify-end gap-3 pt-4">
            <ActionButton action="cancel" onClick={() => setModalOpen(false)} disabled={saving} />
            <ActionButton action="save" variant="primary" onClick={save} disabled={saving} />
          </div>
        }
      >
        <div className="space-y-4 pt-2">
          {error && <div className="text-red-400 text-xs">{error}</div>}
          <div>
            <label className="block text-sm text-gray-300 mb-1">{t('proxyKeyManagement.keyName')}</label>
            <Input
              value={keyName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyName(e.target.value)}
              placeholder={t('proxyKeyManagement.keyNamePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">{t('proxyKeyManagement.createMode')}</label>
            <Radio.Group value={createMode} onChange={(v: string | number | undefined) => setCreateMode((v as 'auto' | 'existing') ?? 'auto')}>
              <Radio value="auto">{t('proxyKeyManagement.autoGenerate')}</Radio>
              <Radio value="existing">{t('proxyKeyManagement.useExisting')}</Radio>
            </Radio.Group>
          </div>
          {createMode === 'existing' && (
            <div>
              <label className="block text-sm text-gray-300 mb-1">{t('proxyKeyManagement.publicKeyContent')}</label>
              <Input.TextArea
                rows={3}
                value={publicKeyText}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPublicKeyText(e.target.value)}
                placeholder={t('proxyKeyManagement.publicKeyPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('proxyKeyManagement.publicKeyTip')}</p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
